import React from 'react';

/**
 * Public contact details, set as a ruled list rather than a row of cards — the
 * hairlines carry the structure and nothing is boxed. Only fields with usable
 * values are rendered, so the list stays tidy when a school has not filled
 * everything in.
 */
const SchoolContactSection = ({ profile }) => {
  const { schoolName, address, phone, website, place } = profile;

  const details = [];

  if (phone) {
    details.push({
      key: 'phone',
      label: 'Telephone',
      numeric: true,
      content: (
        <a
          className="sp-inline-link"
          href={phone.href}
          aria-label={`Call ${schoolName} on ${phone.display}`}
        >
          {phone.display}
        </a>
      ),
    });
  }

  if (website) {
    details.push({
      key: 'website',
      label: 'Website',
      content: (
        <a
          className="sp-inline-link"
          href={website.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Visit the ${schoolName} website (opens in a new tab)`}
        >
          {website.label}
        </a>
      ),
    });
  }

  if (address) {
    details.push({ key: 'address', label: 'Address', content: address });
  }

  if (place) {
    details.push({ key: 'place', label: 'Location', content: place });
  }

  if (details.length === 0) return null;

  return (
    <section className="sp-section" id="contact" aria-labelledby="sp-contact-heading">
      <div className="sp-shell sp-editorial">
        <div className="sp-rail">
          <p className="sp-label">Contact</p>
        </div>

        <div>
          <h2 className="sp-display sp-display-m" id="sp-contact-heading">
            School information
          </h2>

          <dl className="sp-contact-list">
            {details.map(({ key, label, content, numeric }) => (
              <div className="sp-contact-row" key={key}>
                <dt className="sp-label">{label}</dt>
                <dd className={numeric ? 'sp-numeric' : undefined}>{content}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      <style>{`
        .sp-contact-list {
          margin: 2rem 0 0 0;
          padding: 0;
          border-top: 1px solid var(--sp-rule);
        }

        .sp-contact-row {
          display: grid;
          grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);
          gap: 1rem 2rem;
          align-items: baseline;
          padding: 1.15rem 0;
          border-bottom: 1px solid var(--sp-rule);
        }

        .sp-contact-row dt {
          padding-top: 0.15rem;
        }

        .sp-contact-row dd {
          margin: 0;
          font-size: 1.05rem;
          line-height: 1.5;
          color: var(--sp-ink);
          overflow-wrap: anywhere;
        }

        .sp-numeric {
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.01em;
        }

        @media (max-width: 600px) {
          .sp-contact-row {
            grid-template-columns: 1fr;
            gap: 0.4rem;
          }
        }
      `}</style>
    </section>
  );
};

export default SchoolContactSection;
